import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useGameStore = create(
  persist(
    (set, get) => ({
      // App state
      sportType: 'baseball', // 'baseball' or 'softball'
      currentTeamId: null,
      currentGameId: null,
      teams: [],
      games: [],
      seasons: [],
      
      // Actions
      setSportType: (type) => set({ sportType: type }),
      setCurrentTeam: (teamId) => set({ currentTeamId: teamId }),
      setCurrentGame: (gameId) => set({ currentGameId: gameId }),
      
      setTeams: (teams) => set({ teams }),
      addTeam: (team) => set((state) => ({ teams: [...state.teams, team] })),
      updateTeam: (teamId, updates) => set((state) => ({
        teams: state.teams.map(t => t.id === teamId ? { ...t, ...updates } : t)
      })),
      removeTeam: (teamId) => set((state) => ({
        teams: state.teams.filter(t => t.id !== teamId),
        currentTeamId: state.currentTeamId === teamId ? null : state.currentTeamId
      })),
      
      setGames: (games) => set({ games }),
      addGame: (game) => set((state) => ({ games: [...state.games, game] })),
      updateGame: (gameId, updates) => set((state) => ({
        games: state.games.map(g => g.id === gameId ? { ...g, ...updates } : g)
      })),
      removeGame: (gameId) => set((state) => ({
        games: state.games.filter(g => g.id !== gameId),
        currentGameId: state.currentGameId === gameId ? null : state.currentGameId
      })),
      
      setSeasons: (seasons) => set({ seasons }),
      addSeason: (season) => set((state) => ({ seasons: [...state.seasons, season] })),
      
      // Player management within teams
      addPlayerToTeam: (teamId, player) => set((state) => ({
        teams: state.teams.map(t => 
          t.id === teamId 
            ? { ...t, players: [...(t.players || []), player] }
            : t
        )
      })),
      updatePlayerInTeam: (teamId, playerId, updates) => set((state) => ({
        teams: state.teams.map(t =>
          t.id === teamId
            ? {
                ...t,
                players: t.players.map(p =>
                  p.id === playerId ? { ...p, ...updates } : p
                )
              }
            : t
        )
      })),
      removePlayerFromTeam: (teamId, playerId) => set((state) => ({
        teams: state.teams.map(t =>
          t.id === teamId
            ? { ...t, players: t.players.filter(p => p.id !== playerId) }
            : t
        )
      })),
      
      // Get current team
      getCurrentTeam: () => {
        const state = get();
        return state.teams.find(t => t.id === state.currentTeamId);
      },
      
      // Get current game
      getCurrentGame: () => {
        const state = get();
        return state.games.find(g => g.id === state.currentGameId);
      },
      
      // Get max innings based on sport type
      getMaxInnings: () => {
        const state = get();
        return state.sportType === 'softball' ? 7 : 9;
      },

      // Live scoring state — shared across GameMode, Scoring, and Field pages
      liveScoringState: null, // { currentBatterIndex, balls, strikes, outs, inning, isTopInning, runners, homeScore, awayScore, timestamp }
      setLiveScoringState: (scoring) => set({ liveScoringState: { ...scoring, timestamp: Date.now() } }),
      
      // Reset store
      resetStore: () => set({
        sportType: 'baseball',
        currentTeamId: null,
        currentGameId: null,
        teams: [],
        games: [],
        seasons: [],
        liveScoringState: null,
      })
    }),
    {
      name: 'diamond-manager-storage',
      version: 1
    }
  )
);
